import React, { useEffect, useMemo, useState } from "react";
import {
  BookOpen, Music, ScrollText, Search, ExternalLink, Download,
  Bookmark, BookmarkCheck, Sparkles, Clock,
} from "lucide-react";
import { api } from "../../lib/api";

// ---------------- Daily Pāli verses (rotates by date) ----------------
const DAILY_VERSES = [
  {
    pali: "Manopubbaṅgamā dhammā, manoseṭṭhā manomayā.",
    en: "Mind precedes all things; mind is their chief, they are mind-made.",
    ref: "Dhammapada · Verse 1",
  },
  {
    pali: "Sabbe sattā sukhitā hontu.",
    en: "May all beings be happy.",
    ref: "Mettā Sutta",
  },
  {
    pali: "Na hi verena verāni, sammantīdha kudācanaṃ.",
    en: "Hatred is never appeased by hatred in this world.",
    ref: "Dhammapada · Verse 5",
  },
  {
    pali: "Attā hi attano nātho.",
    en: "One is one's own refuge.",
    ref: "Dhammapada · Verse 160",
  },
  {
    pali: "Yo ca buddhañca dhammañca, saṅghañca saraṇaṃ gato.",
    en: "He who has gone for refuge to the Buddha, the Dhamma and the Sangha…",
    ref: "Dhammapada · Verse 190",
  },
  {
    pali: "Sabbadānaṃ dhammadānaṃ jināti.",
    en: "The gift of Dhamma surpasses every other gift.",
    ref: "Dhammapada · Verse 354",
  },
  {
    pali: "Appamādo amatapadaṃ.",
    en: "Heedfulness is the path to the Deathless.",
    ref: "Dhammapada · Verse 21",
  },
];

// ---------------- Curated open-access resources ----------------
const RESOURCES = [
  // Babasaheb's foundational works
  {
    cat: "babasaheb", type: "book",
    title: "The Buddha and His Dhamma",
    author: "Dr. B. R. Ambedkar (1957)",
    note: "The foundational text of the Navayāna tradition, Babasaheb's reinterpretation of the Buddha's teachings for the modern era.",
    url: "https://archive.org/details/in.gov.ignca.15980",
    minutes: 720,
  },
  {
    cat: "babasaheb", type: "book",
    title: "Annihilation of Caste",
    author: "Dr. B. R. Ambedkar (1936)",
    note: "The undelivered speech that became one of the most important critiques of Hindu society ever written.",
    url: "https://archive.org/details/in.ernet.dli.2015.71655",
    minutes: 180,
  },
  {
    cat: "babasaheb", type: "book",
    title: "Riddles in Hinduism",
    author: "Dr. B. R. Ambedkar (1987, posthumous)",
    note: "A forensic examination of contradictions in Hindu scripture.",
    url: "https://archive.org/details/riddles-in-hinduism",
    minutes: 360,
  },
  {
    cat: "babasaheb", type: "book",
    title: "States and Minorities",
    author: "Dr. B. R. Ambedkar (1947)",
    note: "Babasaheb's blueprint for an India that protects every community equally.",
    url: "https://archive.org/details/in.ernet.dli.2015.71317",
    minutes: 240,
  },
  {
    cat: "babasaheb", type: "book",
    title: "Thoughts on Linguistic States",
    author: "Dr. B. R. Ambedkar (1955)",
    note: "Babasaheb's prescient warning on language, federalism and the future of India.",
    url: "https://archive.org/details/in.ernet.dli.2015.143604",
    minutes: 120,
  },
  // Pāli canon
  {
    cat: "buddha", type: "book",
    title: "Dhammapada",
    author: "Translated by Ven. Buddharakkhita",
    note: "423 verses, perhaps the most-read Buddhist text in the world. A complete moral and meditative compass.",
    url: "https://www.accesstoinsight.org/tipitaka/kn/dhp/index.html",
    minutes: 90,
  },
  {
    cat: "buddha", type: "book",
    title: "Sutta Nipāta, Selected Discourses",
    author: "Pāli canon · Khuddaka Nikāya",
    note: "Some of the oldest layers of Buddhist teaching, including the Mettā Sutta.",
    url: "https://www.accesstoinsight.org/tipitaka/kn/snp/index.html",
    minutes: 240,
  },
  {
    cat: "buddha", type: "book",
    title: "Majjhima Nikāya, The Middle-Length Discourses",
    author: "Translated by Ven. Bhikkhu Bodhi",
    note: "152 discourses on practice, for serious students of Theravāda meditation.",
    url: "https://suttacentral.net/mn",
    minutes: 1500,
  },
  {
    cat: "buddha", type: "book",
    title: "Dīgha Nikāya, Long Discourses",
    author: "Translated by Bhikkhu Sujato",
    note: "34 of the longest discourses, including the Mahāparinibbāna Sutta, the Buddha's final days.",
    url: "https://suttacentral.net/dn",
    minutes: 2400,
  },
  {
    cat: "buddha", type: "book",
    title: "What the Buddha Taught",
    author: "Walpola Rahula (1959)",
    note: "A modern classic, clear, scholarly and meditative. The standard introduction for English readers.",
    url: "https://archive.org/details/WhatTheBuddhaTaught_201606",
    minutes: 220,
  },
  // Audio
  {
    cat: "audio", type: "audio",
    title: "Mettā Bhāvanā — Pāli loving-kindness chant",
    author: "Buddhist Pali Chanting collection",
    note: "Traditional Pāli mettā recitation. Slow, steady, suitable for guided loving-kindness practice at home or in a group.",
    url: "https://archive.org/details/MettaChantingPaliViet",
    minutes: 35,
  },
  {
    cat: "audio", type: "audio",
    title: "Buddhist Pāli chanting collection",
    author: "Sri Lankan and Burmese traditions",
    note: "A full collection of traditional Pāli chants — Iti pi so, Mahāmangala, Karaniya Mettā, suitable for morning practice.",
    url: "https://archive.org/details/buddhist-pali-chanting",
    minutes: 60,
  },
  {
    cat: "audio", type: "audio",
    title: "Buddhist chanting — general anthology",
    author: "Various traditions",
    note: "A broader anthology of Buddhist chanting traditions (Pāli, Sanskrit, Tibetan) — gentle, grounding, suitable to begin any sit.",
    url: "https://archive.org/details/buddhist-chanting",
    minutes: 50,
  },
  // Reformers
  {
    cat: "reformers", type: "book",
    title: "Slavery, Gulamgiri",
    author: "Mahatma Jyotirao Phule (1873)",
    note: "Phule's blistering critique of caste, gender oppression and Brahmanical scripture.",
    url: "https://archive.org/details/in.ernet.dli.2015.485854",
    minutes: 200,
  },
  {
    cat: "reformers", type: "book",
    title: "Castes in India — Their Mechanism, Genesis and Development",
    author: "Dr. B. R. Ambedkar (1916)",
    note: "Babasaheb's earliest published paper — the intellectual foundation of his life-long campaign against caste.",
    url: "https://archive.org/details/castesinindia035140mbp",
    minutes: 90,
  },
  {
    cat: "reformers", type: "book",
    title: "The Untouchables, Who Were They?",
    author: "Dr. B. R. Ambedkar (1948)",
    note: "Babasaheb's anthropological account of how untouchability arose.",
    url: "https://archive.org/details/TheUntouchables_1948",
    minutes: 240,
  },
  {
    cat: "reformers", type: "book",
    title: "Buddha or Karl Marx",
    author: "Dr. B. R. Ambedkar (1956)",
    note: "Babasaheb's incisive comparison of the Buddha's ethics with the Marxist project — written in the year of his conversion.",
    url: "https://archive.org/stream/Ambedkar_CompleteWorks/20.Buddha%20or%20Karl%20Marx_djvu.txt",
    minutes: 90,
  },
];

const CATEGORIES = [
  { id: "all", label: "All resources", icon: BookOpen },
  { id: "saved", label: "My bookmarks", icon: Bookmark },
  { id: "babasaheb", label: "Babasaheb's writings", icon: ScrollText },
  { id: "buddha", label: "The Buddha's Dhamma", icon: BookOpen },
  { id: "reformers", label: "Reformers", icon: ScrollText },
  { id: "audio", label: "Audio & chanting", icon: Music },
];

const STORAGE_KEY = "bhimsangha:library:bookmarks";

function getDailyVerse() {
  // Stable per UTC date
  const epoch = Math.floor(Date.now() / 86400000);
  return DAILY_VERSES[epoch % DAILY_VERSES.length];
}

function readingTimeLabel(mins) {
  if (!mins) return "—";
  if (mins < 60) return `${mins} min read`;
  const h = Math.floor(mins / 60);
  const r = mins % 60;
  return r ? `${h}h ${r}m read` : `${h}h read`;
}

export default function PlatformLibrary() {
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch { return []; }
  });
  const verse = useMemo(getDailyVerse, []);

  // Hydrate bookmarks from server on mount; localStorage was the seed
  useEffect(() => {
    let alive = true;
    api.get("/platform/library/bookmarks")
      .then((r) => {
        if (!alive) return;
        const server = Array.isArray(r.data?.bookmarks) ? r.data.bookmarks : [];
        setBookmarks(server);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(server));
      })
      .catch(() => { /* fall back to localStorage value already in state */ });
    return () => { alive = false; };
  }, []);

  // Optimistic toggle — update local state + persist via API + mirror to localStorage
  const toggleBookmark = (title) => {
    setBookmarks((prev) => {
      const isSaved = prev.includes(title);
      const next = isSaved ? prev.filter((t) => t !== title) : [...prev, title];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      // Fire and forget; revert on failure would be ideal but UX-wise this is fine
      if (isSaved) {
        api.delete(`/platform/library/bookmarks?title=${encodeURIComponent(title)}`).catch(() => {});
      } else {
        api.post("/platform/library/bookmarks", { title }).catch(() => {});
      }
      return next;
    });
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return RESOURCES.filter((r) => {
      if (cat === "saved") {
        if (!bookmarks.includes(r.title)) return false;
      } else if (cat !== "all" && r.cat !== cat) return false;
      if (term && !`${r.title} ${r.author} ${r.note}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [cat, q, bookmarks]);

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-[1500px] mx-auto" data-testid="platform-library">
      <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">Open · Public-domain · Free forever</p>
      <h1 className="font-display text-4xl text-navy">Dhamma Library</h1>
      <p className="mt-1 text-navy/65 max-w-2xl">
        Babasaheb's writings, the Pāli canon, audio chanting and reformer texts, all curated and
        linked from open archives. Click any title to read or download.
      </p>

      {/* Daily Verse hero */}
      <div
        className="mt-7 relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#0B1F4D] via-[#1a2f6b] to-[#6C4EFF] text-pearl p-7 sm:p-9 border border-white/10 shadow-xl"
        data-testid="library-daily-verse"
      >
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle at 90% 10%, rgba(212,166,74,0.55) 0%, transparent 55%)' }}
        />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-saffron">
            <Sparkles size={12} /> Verse for today
          </div>
          <p className="mt-3 font-display text-2xl sm:text-3xl text-pearl leading-snug italic">
            "{verse.pali}"
          </p>
          <p className="mt-3 text-pearl/85 text-base sm:text-lg max-w-3xl">{verse.en}</p>
          <div className="mt-3 text-[11px] uppercase tracking-widest text-pearl/55">{verse.ref}</div>
        </div>
      </div>

      {/* Search + count */}
      <div className="mt-7 flex flex-col md:flex-row md:items-center gap-4">
        <div className="relative flex-1 max-w-xl">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title, author, theme…"
            data-testid="library-search"
            className="form-input w-full pl-9"
          />
        </div>
        <div className="text-xs text-navy/55 italic">
          {filtered.length} {filtered.length === 1 ? "resource" : "resources"}
          {bookmarks.length > 0 && cat !== "saved" && (
            <span className="ml-2 text-saffron">· {bookmarks.length} bookmarked</span>
          )}
        </div>
      </div>

      {/* Categories */}
      <div className="mt-5 flex flex-wrap gap-2" data-testid="library-categories">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const active = cat === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              data-testid={`library-cat-${c.id}`}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm border transition-colors ${
                active
                  ? "bg-navy text-pearl border-navy"
                  : "bg-white text-navy/70 border-navy/15 hover:border-saffron hover:text-saffron"
              }`}
            >
              <Icon size={14} />
              {c.label}
              {c.id === "saved" && bookmarks.length > 0 && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-saffron text-navy" : "bg-saffron/20 text-saffron"}`}>
                  {bookmarks.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Resource grid */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {filtered.map((r) => {
          const saved = bookmarks.includes(r.title);
          return (
            <div
              key={r.title}
              data-testid={`library-item-${r.title.slice(0, 30).replace(/\s+/g, "-").toLowerCase()}`}
              className="group bg-white border border-navy/10 rounded-xl p-5 hover:border-saffron transition-colors hover:-translate-y-0.5 shadow-sm hover:shadow-md flex flex-col relative"
            >
              <button
                onClick={() => toggleBookmark(r.title)}
                aria-label={saved ? "Remove bookmark" : "Add bookmark"}
                data-testid={`library-bookmark-${r.title.slice(0, 30).replace(/\s+/g, "-").toLowerCase()}`}
                className={`absolute top-4 right-4 p-1.5 rounded-md transition-colors ${
                  saved
                    ? "text-saffron hover:bg-saffron/10"
                    : "text-navy/30 hover:text-saffron hover:bg-saffron/10"
                }`}
              >
                {saved ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
              </button>

              <div className="flex items-center gap-2 pr-8">
                <div className="text-[10px] uppercase tracking-widest text-saffron">{r.cat}</div>
                {r.type === "audio" ? (
                  <Music size={14} className="text-navy/40" />
                ) : (
                  <BookOpen size={14} className="text-navy/40" />
                )}
              </div>
              <h3 className="mt-2 font-display text-lg text-navy leading-tight pr-8">{r.title}</h3>
              <p className="mt-1 text-xs text-navy/55 italic">{r.author}</p>
              <p className="mt-3 text-sm text-navy/75 leading-relaxed flex-1">{r.note}</p>

              <div className="mt-4 flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1 text-navy/50">
                  <Clock size={12} /> {readingTimeLabel(r.minutes)}
                </span>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-saffron group-hover:text-saffron-light"
                >
                  <Download size={13} /> Open <ExternalLink size={11} />
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="mt-16 text-center text-navy/55" data-testid="library-empty-state">
          {cat === "saved"
            ? "You haven't bookmarked anything yet, tap the bookmark icon on any resource to save it here."
            : `No resources match "${q}". Try a different word or clear the search.`}
        </div>
      )}

      <p className="mt-12 text-xs text-navy/45 italic max-w-2xl">
        All texts are linked from public archives such as archive.org, accesstoinsight.org and
        suttacentral.net. ABGN-UK does not host these works directly; we curate the most reliable
        editions for our community.
      </p>
    </div>
  );
}
